import { DataModel, ReferenceModel } from "../../base/index.js";
import { type AsyncResourceVariant, provideReact } from "../../react/index.js";
import { config } from "../../config/config.js";
import type { MailSettingsData } from "./types.js";
import { classes } from "polytype";
import assertObjectFound from "../../base/assertObjectFound.js";

export class MailSettings extends ReferenceModel {
  public static ofId(projectId: string) {
    return new MailSettings(projectId);
  }

  public static find = provideReact(async (projectId: string) => {
    const data = await config.behaviors.mailSettings.find(projectId);
    if (data !== undefined) {
      return new MailSettingsDetailed(data);
    }
  });

  public static get = provideReact(async (projectId: string) => {
    const settings = await this.find(projectId);
    assertObjectFound(settings, "mailSettings", projectId);
    return settings;
  });

  public findDetailed = provideReact(
    () => MailSettings.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<MailSettingsDetailed | undefined>>;

  public getDetailed = provideReact(
    () => MailSettings.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<MailSettingsDetailed>>;

  public async updateBlocklist(addresses: string[]) {
    return await config.behaviors.mailSettings.updateBlocklist(
      this.id,
      addresses,
    );
  }

  public async updateAllowlist(addresses: string[]) {
    return await config.behaviors.mailSettings.updateAllowlist(
      this.id,
      addresses,
    );
  }
}

export class MailSettingsDetailed extends classes(
  DataModel<MailSettingsData>,
  MailSettings,
) {
  public readonly blocklist: string[];
  public readonly allowlist: string[];
  public constructor(data: MailSettingsData) {
    super([data], [data.projectId]);
    this.blocklist = data.blacklist;
    this.allowlist = data.whitelist;
  }

  public async addBlocklistEntry(address: string) {
    const response = await this.updateBlocklist([...this.blocklist, address]);

    if (response) {
      return response.map((e) => ({
        ...e,
        path: e.path.includes("entry") ? "address" : e.path,
      }));
    }
  }

  public async removeBlocklistEntry(address: string) {
    await this.updateBlocklist(this.blocklist.filter((a) => a !== address));
  }

  public async addAllowlistEntry(address: string) {
    const response = await this.updateAllowlist([...this.allowlist, address]);

    if (response) {
      return response.map((e) => ({
        ...e,
        path: e.path.includes("entry") ? "address" : e.path,
      }));
    }
  }

  public async removeAllowlistEntry(address: string) {
    await this.updateAllowlist(this.allowlist.filter((a) => a !== address));
  }
}
